import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/Brand";
import { Eye, EyeSlash, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/invites/lookup/${token}`)
      .then(({ data }) => {
        if (cancelled) return;
        setInvite(data);
        setName(data.name || "");
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.detail || e.message);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/invites/accept/${token}`, { password, name });
      toast.success("Welcome to Strata");
      // Auto sign-in
      try {
        await login(invite.email, password);
        navigate("/dashboard");
      } catch {
        navigate("/login");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8] text-sm text-slate-500">
        Loading invite…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F6F8] px-4">
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center max-w-md w-full" data-testid="invite-error">
          <Warning size={28} className="text-amber-500 mx-auto" />
          <h2 className="mt-3 font-display text-xl font-bold text-slate-900">Invite unavailable</h2>
          <p className="mt-2 text-sm text-slate-500">{error}</p>
          <Link
            to="/login"
            className="mt-6 inline-flex text-sm font-semibold text-[#327de3] hover:text-[#1f64c2]"
            data-testid="invite-error-back"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col px-6 sm:px-12 lg:px-20 py-10 justify-center">
        <div className="max-w-md w-full mx-auto">
          <div className="mb-10 flex justify-start">
            <Brand size="md" color="dark" />
          </div>

          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
            <CheckCircle size={14} weight="fill" /> Invite verified
          </div>

          <h1 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Accept your invitation
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            <strong className="text-slate-800">{invite.invited_by_name}</strong> invited you to join Strata as
            a <strong className="text-slate-800">{invite.role_label}</strong>. Set a password to finish creating your account.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="invite-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Email</Label>
              <Input value={invite.email} disabled className="h-11 bg-slate-50" data-testid="invite-email" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="invite-name" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Your name</Label>
              <Input
                id="invite-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="h-11"
                data-testid="invite-name-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="invite-pwd" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Password</Label>
              <div className="relative">
                <Input
                  id="invite-pwd"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="At least 6 characters"
                  className="h-11 pr-10"
                  data-testid="invite-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="invite-toggle-password"
                  aria-label="Toggle password"
                >
                  {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="invite-confirm" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Confirm password</Label>
              <Input
                id="invite-confirm"
                type={showPwd ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                placeholder="Re-enter password"
                className="h-11"
                data-testid="invite-confirm-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[#327de3] hover:bg-[#1f64c2] text-white font-medium rounded-lg"
              data-testid="invite-submit-button"
            >
              {submitting ? "Creating your account…" : "Accept invite & sign in"}
            </Button>

            <p className="text-xs text-slate-400">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-[#327de3] hover:text-[#1f64c2]">Sign in instead</Link>
            </p>
          </form>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden bg-[#1f64c2]">
        <img
          src="https://images.unsplash.com/photo-1696029475209-b759293ed2b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGJsdWUlMjBhcmNoaXRlY3R1cmUlMjBnbGFzc3xlbnwwfHx8fDE3Nzc5NDUyMzN8MA&ixlib=rb-4.1.0&q=85"
          alt="Strata"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#173f8a]/80 via-[#1f64c2]/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-display text-3xl font-bold tracking-tight max-w-md">
            One dashboard. Every business.
          </h2>
          <p className="mt-3 text-sm text-white/80 max-w-md">
            Welcome to the Friedman Group financial command center.
          </p>
        </div>
      </div>
    </div>
  );
}
